import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import Table from 'react-bootstrap/Table';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import { Toggle } from 'uikit/Toggle/Toggle';
import './FeaturesTab.scss';

const FeaturesTab = ({ isLoading, customerNumber, features, getCustomerFeatures, toggleFeature }) => {
  const i18n = useContext(i18nContext);
  const [pendingFeatures, setPendingFeatures] = useState(new Set());

  useEffect(() => {
    getCustomerFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!features) {
    return <Loader className={'application-loader features-tab-loader'} />;
  }

  const handleToggleFeature = async (feature, currentState) => {
    // Prevent multiple clicks on the same feature
    if (pendingFeatures.has(feature)) {
      return;
    }

    setPendingFeatures((prev) => new Set(prev).add(feature));

    try {
      await toggleFeature(customerNumber, feature, !currentState);
    } finally {
      setPendingFeatures((prev) => {
        const next = new Set(prev);
        next.delete(feature);
        return next;
      });
    }
  };

  return (
    <div className={'features-tab-container'}>
      <div className={'features-tab-header'}>
        <h3>{i18n.getMessage('featuresTab.title')}</h3>
        <p className={'features-tab-description'}>{i18n.getMessage('featuresTab.description')}</p>
      </div>

      <Table responsive className={'features-table'}>
        <thead>
          <tr>
            <th>{i18n.getMessage('featuresTab.table.feature')}</th>
            <th>{i18n.getMessage('featuresTab.table.description')}</th>
            <th>{i18n.getMessage('featuresTab.table.status')}</th>
          </tr>
        </thead>
        <tbody>
          {features.map((feature, index) => (
            <tr key={index} className={'features-table-row'}>
              <td className={'features-table-cell feature-name'}>
                <strong>{feature.feature}</strong>
              </td>
              <td className={'features-table-cell feature-description'}>{feature.description}</td>
              <td className={'features-table-cell feature-toggle'}>
                <Toggle
                  isEnabled={feature.enabled}
                  onChange={() => handleToggleFeature(feature.feature, feature.enabled)}
                  disabled={isLoading || pendingFeatures.has(feature.feature)}
                  label={
                    feature.enabled
                      ? i18n.getMessage('featuresTab.status.on')
                      : i18n.getMessage('featuresTab.status.off')
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {features.length === 0 && <div className={'features-tab-empty'}>{i18n.getMessage('featuresTab.noFeatures')}</div>}
    </div>
  );
};

FeaturesTab.propTypes = {
  isLoading: PropTypes.bool,
  customerNumber: PropTypes.string.isRequired,
  features: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  getCustomerFeatures: PropTypes.func.isRequired,
  toggleFeature: PropTypes.func.isRequired
};

export default inject((stores) => ({}))(observer(FeaturesTab));
